import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Calculator, Layers, Thermometer, FileCheck, Award } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const UValueCalculations = () => {
  const faqs = [
    {
      question: "What is a U-Value?",
      answer: "A U-value measures how effective a building element is as an insulator. It represents the rate of heat transfer through a structure, measured in W/m²K (watts per square metre per degree Kelvin). Lower U-values indicate better insulation. Building Regulations set maximum U-values for walls, floors, roofs, windows and doors."
    },
    {
      question: "When do I need U-Value Calculations?",
      answer: "U-value calculations are required for Building Regulations compliance on new builds, extensions and renovations. They're needed for SAP calculations, to demonstrate Part L compliance, for planning applications involving energy performance, and when specifying non-standard construction build-ups."
    },
    {
      question: "What U-Values does Part L require?",
      answer: "Part L 2021 sets limiting U-values: Walls 0.26 W/m²K, Floors 0.18 W/m²K, Roofs 0.16 W/m²K, Windows/Doors 1.6 W/m²K. However, achieving the Target Fabric Energy Efficiency Standard (TFEE) often requires better performance than these limiting values."
    },
    {
      question: "How is a U-Value calculated?",
      answer: "U-values are calculated by analysing each layer of a construction element—materials, thicknesses, thermal conductivities—plus surface resistances and any air gaps. We use industry-standard methods (BR 443, BS EN ISO 6946) and software to produce accurate, compliant calculations."
    },
    {
      question: "What information do you need?",
      answer: "We need detailed construction specifications: material types, thicknesses, densities where relevant, and the complete build-up from internal to external face. For windows and doors, we need frame types, glazing specifications and overall dimensions. Manufacturer data sheets are helpful."
    }
  ];

  return (
    <>
      <Helmet>
        <title>U-Value Calculations | Thermal Performance | Building Regulations | Energy Performance Direct</title>
        <meta name="description" content="Professional U-value calculations for Building Regulations compliance. Walls, floors, roofs, windows and doors. Part L compliant thermal analysis. From £50. Call 01392 980077." />
        <meta name="keywords" content="U-value calculations, thermal performance, Part L compliance, building regulations thermal, wall U-value, floor U-value, roof U-value, window U-value" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/u-value-calculations" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-blue-600">Thermal Performance Analysis</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                U-Value Calculations
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Professional thermal transmittance calculations for walls, floors, roofs, windows and doors. Building Regulations Part L compliance demonstrated.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium">From £50</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium">BR 443 Compliant</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium">24-48hr Turnaround</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                    Request U-Value Quote
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button size="lg" variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                    <Phone className="mr-2 h-5 w-5" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">U-Value Calculation Services</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Layers className="h-12 w-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Wall U-Values</h3>
                    <p className="text-gray-600">
                      Calculations for all wall constructions: cavity walls, solid walls, timber frame, SIPs, ICF, steel frame and non-standard build-ups. Part L limiting value: 0.26 W/m²K.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Layers className="h-12 w-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Floor U-Values</h3>
                    <p className="text-gray-600">
                      Ground floor U-value calculations including perimeter/area ratio effects. Suspended floors, solid slabs, beam and block. Part L limiting value: 0.18 W/m²K.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Layers className="h-12 w-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Roof U-Values</h3>
                    <p className="text-gray-600">
                      Pitched roofs (between/over rafters), flat roofs (warm/cold deck), room-in-roof constructions. Part L limiting value: 0.16 W/m²K.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Thermometer className="h-12 w-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Window U-Values</h3>
                    <p className="text-gray-600">
                      Window and door U-value calculations. Frame factors, glazing performance, overall Uw values. Part L limiting value: 1.6 W/m²K for windows.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Calculator className="h-12 w-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Non-Standard Elements</h3>
                    <p className="text-gray-600">
                      Bespoke calculations for unusual constructions, heritage buildings, retrofit specifications and innovative building systems.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <FileCheck className="h-12 w-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">SAP Integration</h3>
                    <p className="text-gray-600">
                      U-value calculations formatted for direct input into SAP assessments. Consistent methodology ensures accurate energy modelling.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Part L 2021 U-Value Requirements</h2>
              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-lg shadow">
                  <thead className="bg-blue-600 text-white">
                    <tr>
                      <th className="px-6 py-3 text-left">Element</th>
                      <th className="px-6 py-3 text-left">Limiting U-Value</th>
                      <th className="px-6 py-3 text-left">Typical Target</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4">External Walls</td>
                      <td className="px-6 py-4">0.26 W/m²K</td>
                      <td className="px-6 py-4">0.18-0.22 W/m²K</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4">Ground Floors</td>
                      <td className="px-6 py-4">0.18 W/m²K</td>
                      <td className="px-6 py-4">0.13-0.15 W/m²K</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4">Roofs</td>
                      <td className="px-6 py-4">0.16 W/m²K</td>
                      <td className="px-6 py-4">0.11-0.13 W/m²K</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4">Windows</td>
                      <td className="px-6 py-4">1.6 W/m²K</td>
                      <td className="px-6 py-4">1.2-1.4 W/m²K</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4">Doors</td>
                      <td className="px-6 py-4">1.6 W/m²K</td>
                      <td className="px-6 py-4">1.0-1.4 W/m²K</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-500 mt-4 text-center">Note: Limiting values are maximums. Achieving TFEE compliance typically requires better performance.</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">U-Value Calculation FAQs</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-blue-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Need U-Value Calculations?</h2>
            <p className="text-xl mb-8 opacity-90">
              Professional thermal performance analysis for Building Regulations compliance
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking">
                <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100">
                  Request Quote
                </Button>
              </Link>
              <a href="tel:01392980077">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-700">
                  <Phone className="mr-2 h-5 w-5" />
                  01392 980077
                </Button>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default UValueCalculations;
